import { RpcError } from '@protobuf-ts/runtime-rpc'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BaseActionForm } from 'components/pages/task/action/form/BaseActionForm'
import { getDeclinePresets, getDenyPresets, getDenyText, getRemovePresets, getSuccessMessage } from 'components/pages/task/action/messages'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import React, { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

type DenyWithCommentTaskDialog = {
    task: TaskValue
} & ClosableProps

export const DenyWithCommentTaskDialog: FC<DenyWithCommentTaskDialog> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const getLabels = useCallback((): { title: string; description: string, presets: string[] } => {
        switch (task.type?.taskType.oneofKind) {
            case 'certify':
                return {
                    title: intl.formatMessage({
                        id: 'component_task_action_form_remove_title',
                        defaultMessage: 'Remove access with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_remove_description',
                        defaultMessage: 'Add your comment to let others know why you recommend removing this access.',
                    }),
                    presets: getRemovePresets(intl),
                }
            case 'revoke':
                return {
                    title: intl.formatMessage({
                        id: 'component_task_action_form_decline_title',
                        defaultMessage: 'Decline revocation with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_decline_description',
                        defaultMessage: 'Add your comment to let others know why you declined this revocation.',
                    }),
                    presets: getDeclinePresets(intl),
                }
            case 'grant':
            default:
                // This is more generic
                return {
                    title: intl.formatMessage({
                        id: 'component_task_list_deny_comment_option_label',
                        defaultMessage: 'Deny access with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_deny_description',
                        defaultMessage: 'Add your comment to let others know why you denied this access.',
                    }),
                    presets: getDenyPresets(intl),
                }
        }
    }, [intl, task.type?.taskType.oneofKind])

    const handleSave = useCallback(
        async (comment: string): Promise<void> => {
            try {
                await ticketActionTracker?.deny(task, comment)
            } catch (err) {
                return toast(
                    intl.formatMessage(
                        {
                            id: 'task_deny_error',
                            defaultMessage: 'Error denying task. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                )
            }

            toast(
                getSuccessMessage({
                    actionType: 'deny',
                    intl,
                    task,
                }),
                'success',
            )

            close()
        },
        [toast, intl, task, close, ticketActionTracker],
    )

    return (
        <BaseActionForm
            closeDialog={close}
            open
            onDeny={handleSave}
            title={getLabels().title}
            description={getLabels().description}
            presets={getLabels().presets}
            actionText={getDenyText(intl, task)}
            isActionLoading={!ticketActionTracker}
        />
    )
}
